import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import withRouter from '../../components/withRouter';
import { Tag } from '../../types/tag';
import { getTag } from '../../api/tagApi';
import { useTagForm } from '../../hooks/useTagForm';
import { useFormGuard } from '../../hooks/useFormGuard';
import FormGuardModal from '../../components/FormGuardModal';
import '../../components/FormGuardModal.css';
import {
    GeneralSettingsTab,
    PrebidTab,
    AnalyticsTab,
    AdpFormatsTab,
    GptFormatsTab,
    BlockedPagesTab,
    ThirdPartyScriptsTab,
    PowerUpsTab
} from './components';
import AdBlockTab from './components/AdBlockTab';

const Index = (props: any) => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [data, setData] = useState<Tag|null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [activeTab, setActiveTab] = useState('1');

    useEffect(() => {
        if (id) {
            getTag(id).then((res) => {
                setData(res);
            }).finally(() => {
                setLoading(false);
            });
        }
    }, [id]);

    document.title = 'TAG | Ad Pulse';

    const { handleForm, loading: formLoading, isDirty } = useTagForm(data, (updatedData) => {
        setData(updatedData);
    });

    // Form guard for unsaved changes
    const formGuard = useFormGuard({
        isDirty,
        onSave: async () => {
            await handleForm.handleSubmit();
        },
        isLoading: formLoading
    });

    // Set up navigation callback
    useEffect(() => {
        (window as any).__pendingNavigateCallback = navigate;
        return () => {
            delete (window as any).__pendingNavigateCallback;
        };
    }, [navigate]);

    // Keyboard shortcut to save (Ctrl+S)
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
            const isCtrlOrMeta = isMac ? e.metaKey : e.ctrlKey || e.metaKey; // metaKey for some Linux/ChromeOS
            if (isCtrlOrMeta && e.key.toLowerCase() === 's') {
                e.preventDefault();
                if (!formLoading && isDirty) {
                    handleForm.handleSubmit();
                }
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [handleForm, formLoading, isDirty]);

    // Override the default navigate function to use form guard
    const safeNavigate = (path: string) => {
        if (isDirty && !formLoading) {
            formGuard.setShowConfirmModal(true);
            formGuard.setPendingNavigation(path);
        } else {
            navigate(path);
        }
    };

    const toggleTab = (tab: string) => {
        if (activeTab !== tab) setActiveTab(tab);
    };

    // Check if prebid power up is enabled - use useMemo to ensure recalculation
    const isPrebidPowerUpEnabled = React.useMemo(() => {
        return handleForm.values.powerUps?.some((powerUp: any) => powerUp.name === 'prebid' && powerUp.enabled) || false;
    }, [handleForm.values.powerUps]);
    
    const isRealTimeAnalyticsEnabled = React.useMemo(() => {
        return handleForm.values.powerUps?.some((powerUp: any) => powerUp.name === 'realtime-monitoring' && powerUp.enabled) || false;
    }, [handleForm.values.powerUps]);

    const isAdBlockDetectionEnabled = React.useMemo(() => {
        return handleForm.values.powerUps?.some((powerUp: any) => powerUp.name === 'adblock-detection' && powerUp.enabled) || false;
    }, [handleForm.values.powerUps]);

    const tabsConfig = React.useMemo(() => [
        { id: '1', label: 'Configurações', icon: '⚙️', component: GeneralSettingsTab },
        ...(isPrebidPowerUpEnabled ? [{ id: '2', label: 'Prebid.js', icon: '🎯', component: PrebidTab }] : []),
        ...(isRealTimeAnalyticsEnabled ? [{ id: '3', label: 'Analytics', icon: '📊', component: AnalyticsTab }] : []),
        ...(isAdBlockDetectionEnabled ? [{ id: '4', label: 'Ad block', icon: '🚫', component: AdBlockTab }] : []),
        { id: '5', label: 'Formatos', icon: '📱', component: AdpFormatsTab },
        { id: '6', label: 'GPT Tag', icon: '📱', component: GptFormatsTab },
        { id: '7', label: 'Páginas Bloqueadas', icon: '🚫', component: BlockedPagesTab },
        { id: '8', label: 'Scripts de Terceiros', icon: '🔌', component: ThirdPartyScriptsTab },
        { id: '9', label: 'Power Ups', icon: '⚡', component: PowerUpsTab }
    ], [isPrebidPowerUpEnabled, isRealTimeAnalyticsEnabled, isAdBlockDetectionEnabled]);

    // Auto-switch to first available tab if current tab is no longer available
    React.useEffect(() => {
        if (!tabsConfig.some(tab => tab.id === activeTab)) {
            setActiveTab('1');
        }
    }, [tabsConfig, activeTab]);

    const renderTabContent = () => {
        const activeTabConfig = tabsConfig.find(tab => tab.id === activeTab);
        if (activeTabConfig) {
            const TabComponent = activeTabConfig.component;
            return <TabComponent handleForm={handleForm} />;
        }
        return null;
    };

    return (
        <React.Fragment>
            <div className="page-content">
                <div className="container-fluid">
                    <div className="row">
                        <div className="col-lg-12">
                            <div className="card">
                                {isDirty && (
                                    <div className="alert alert-info mb-0 border-0 rounded-0" style={{
                                        background: 'linear-gradient(90deg, #d1ecf1 0%, #bee5eb 100%)',
                                        borderLeft: '4px solid #0dcaf0',
                                        fontSize: '0.875rem'
                                    }}>
                                        <div className="d-flex align-items-center justify-content-between">
                                            <div className="d-flex align-items-center">
                                                <span className="me-2">📝</span>
                                                <span>
                                                    <strong>Alterações não salvas</strong> - Suas modificações serão perdidas se você sair da página.
                                                </span>
                                            </div>
                                            <small className="text-muted">
                                                Pressione{' '}
                                                <kbd>{navigator.platform.includes('Mac') ? '⌘' : 'Ctrl'}</kbd>
                                                +<kbd>S</kbd> para salvar
                                            </small>
                                        </div>
                                    </div>
                                )}
                                <div className="card-body">
                                    <ul className="nav nav-tabs nav-justified mb-3">
                                        {tabsConfig.map(tab => (
                                            <li key={tab.id} className="nav-item" role="presentation">
                                                <button
                                                    type="button"
                                                    className={`nav-link ${activeTab === tab.id ? 'active' : ''}`}
                                                    onClick={() => toggleTab(tab.id)}
                                                >
                                                    <span style={{ marginRight: '6px' }}>{tab.icon}</span>
                                                    {tab.label}
                                                </button>
                                            </li>
                                        ))}
                                    </ul>

                                    {loading ? (
                                        <div className="adp-loading-container">
                                            <div className="adp-loading-spinner"></div>
                                            <span>Carregando configurações...</span>
                                        </div>
                                    ) : (
                                        <form onSubmit={handleForm.handleSubmit} noValidate>
                                            <div className="tab-content">
                                                <div className={`tab-pane ${activeTab ? 'active' : ''}`}>
                                                    {renderTabContent()}
                                                </div>
                                            </div>

                                            <div className="card-footer text-end mt-3 gap-2 d-flex justify-content-end">
                                                <button
                                                    type="button"
                                                    className="btn btn-secondary"
                                                    onClick={() => safeNavigate('/tag')}
                                                    disabled={formLoading}
                                                >
                                                    Cancelar
                                                </button>

                                                <button
                                                    type="submit"
                                                    className="btn btn-primary"
                                                    disabled={formLoading}
                                                >
                                                    {formLoading && <div className="adp-spinner"></div>}
                                                    Salvar Tag
                                                </button>
                                            </div>
                                        </form>
                                    )}

                                    {/* Form Guard Modal */}
                                    <FormGuardModal
                                        isOpen={formGuard.showConfirmModal}
                                        onStayOnPage={formGuard.handleStayOnPage}
                                        onLeaveWithoutSaving={formGuard.handleNavigate}
                                        onSaveAndContinue={formGuard.handleSaveAndContinue}
                                        isProcessing={formGuard.isProcessing}
                                        hasSaveAction={true}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </React.Fragment>
    );
};

export default withRouter(Index);